import React, { Fragment, lazy, useEffect } from "react";
import ReactDOM from "react-dom";
// Overlay styles must be available before any lazy-loaded dashboard route opens.
import "antd/dist/antd.css";
import "./index.scss";
import "./style.css";
import RouteBoundary from "./components/RouteBoundary";
import LoginApp from "./components/login";
import { Provider } from "react-redux";
import store from "./store";
import { BrowserRouter, Switch, Route, Redirect } from "react-router-dom";
import ScrollToTop from "./layout/scroll_to_top";
import Login from "./components/login/login";
import ForgotPassword from "./components/login/ForgotPassword";
import ResetPassword from "./components/login/ResetPassword";
import { loadUser } from "./api/login/auth";
import setAuthToken from "./util/setAuthToken";
import Notfound from "./components/404";
import { useHasActivePlan } from "./customHooks/useHasActivePlan";

// Load the dashboard layout and pages only when their routes are rendered.
const DashboardLayout = lazy(() => import("./components/app"));
const Companymanagment = lazy(() =>
  import("./components/dashboard/companyManagement")
);
const AccessManagement = lazy(() =>
  import("./components/dashboard/accessManagement")
);
const UserManagement = lazy(() =>
  import("./components/dashboard/userManagement")
);
const ProjectManagement = lazy(() =>
  import("./components/dashboard/projectManagement")
);
const ProfileManagement = lazy(() =>
  import("./components/dashboard/profileManagement")
);
const Inventory = lazy(() => import("./components/dashboard/inventory"));
const Plan = lazy(() => import("./components/dashboard/Inspection/plan"));
const InspectionMap = lazy(() =>
  import("./components/dashboard/Inspection/map")
);
const Inspection = lazy(() => import("./components/dashboard/Inspection"));
const CreatePlan = lazy(() =>
  import("./components/dashboard/Inspection/plan/Create")
);
const Turbine = lazy(() =>
  import("./components/dashboard/Inspection/capture/turbine")
);
const ImageGallery = lazy(() =>
  import("./components/dashboard/Inspection/capture/ImageSection")
);
const CaptureSummary = lazy(() =>
  import("./components/dashboard/Inspection/capture/summary")
);
const Capture = lazy(() => import("./components/dashboard/Inspection/capture"));
const ProcessSummary = lazy(() =>
  import("./components/dashboard/Inspection/analyse")
);
const Annotation = lazy(() =>
  import("./components/dashboard/Inspection/analyse/ImageSection")
);
const Review = lazy(() =>
  import("./components/dashboard/Inspection/analyse/Review")
);
const Process = lazy(() => import("./components/dashboard/Inspection/process"));
const Report = lazy(() => import("./components/dashboard/Inspection/report"));
const VegetationReport = lazy(() =>
  import("./components/dashboard/vegetationInspection/report")
);
const LidarReport = lazy(() =>
  import("./components/dashboard/lidarManagement/report")
);
const LidarAnalyse = lazy(() =>
  import("./components/dashboard/lidarManagement/analyse")
);
const VegetationReportMap = lazy(() =>
  import("./components/dashboard/vegetationInspection/mapbox")
);
const LidarReportMap = lazy(() =>
  import("./components/dashboard/lidarManagement/report/Lidar")
);
const Data = lazy(() => import("./components/dashboard/Inspection/data"));
const DataVegetation = lazy(() =>
  import("./components/dashboard/vegetationInspection/data")
);
const Enhanced = lazy(() =>
  import("./components/dashboard/Inspection/process/Enhanced")
);
const Summary = lazy(() =>
  import("./components/dashboard/Inspection/data/summary")
);
const SummaryVegetation = lazy(() =>
  import("./components/dashboard/vegetationInspection/data/summary")
);
const SummaryLidar = lazy(() =>
  import("./components/dashboard/lidarManagement/data/summary")
);
const DataImport = lazy(() =>
  import("./components/dashboard/Inspection/data/import")
);
const DataImportVegetation = lazy(() =>
  import("./components/dashboard/vegetationInspection/data/import")
);
const DataImportLidar = lazy(() =>
  import("./components/dashboard/lidarManagement/data/import")
);
const GalleryLidar = lazy(() =>
  import("./components/dashboard/lidarManagement/data")
);
const AssetCreation = lazy(() =>
  import("./components/dashboard/Inspection/assetCreation")
);
const AssetClass = lazy(() =>
  import("./components/dashboard/Inspection/assetCreation/summary")
);
const AssetCreationAdd = lazy(() =>
  import("./components/dashboard/Inspection/assetCreation/addAssetType")
);
const AssetCreationEdit = lazy(() =>
  import("./components/dashboard/Inspection/assetCreation/editAssetTypes")
);
const DefaultConfiguration = lazy(() =>
  import("./components/dashboard/defaultConfiguration")
);
const ClientConfiguration = lazy(() =>
  import("./components/dashboard/clientConfiguration")
);
const DefaultConfigurationPlan = lazy(() =>
  import("./components/dashboard/Inspection/plan/defaultConfiguration")
);
const Portfolio = lazy(() =>
  import("./components/dashboard/AssetHealth/Portfolio")
);
const AssetsDash = lazy(() =>
  import("./components/dashboard/AssetHealth/Asset")
);
const DamageDash = lazy(() =>
  import("./components/dashboard/AssetHealth/Damage")
);
const Dashboard = lazy(() => import("./components/dashboard/Dashboard"));
const ReportsGeneration = lazy(() =>
  import("./components/dashboard/Inspection/report/reportsGeneration")
);
const AnalseTool = lazy(() =>
  import("./components/dashboard/Inspection/analyse/analyseTool/")
);
const ChangeAnalysis = lazy(() =>
  import("./components/dashboard/Inspection/analyse/ChangeAnalysis")
);

const App = (props) => (
  <RouteBoundary>
    <DashboardLayout {...props} />
  </RouteBoundary>
);

const RequirePlan = ({ children }) => {
  const hasActivePlan = useHasActivePlan();
  if (!hasActivePlan) return <Redirect to="/inspection" />;
  return children;
};

const Root = (props) => {
  if (localStorage.token) {
    setAuthToken(localStorage.token);
  }
  useEffect(() => {
    store.dispatch(loadUser());
  }, []);
  return (
    <Fragment>
      <Provider store={store}>
        <BrowserRouter basename={`/`}>
          <ScrollToTop />
          <Switch>
            <Route exact path="/">
              <Redirect to="/login" />
            </Route>
            <Route exact path="/vegetation/inspection/report">
              <Redirect to="/vegetation/inspection/result" />
            </Route>
            <Route exact path="/inspection/report">
              <Redirect to="/inspection/result" />
            </Route>
            {/* dashboar */}
            <Route exact path="/home">
              <App>
                <Dashboard />
              </App>
            </Route>
            <Route exact path="/page-not-found">
              <Notfound />
            </Route>
            <Route exact path="/company-management">
              <App>
                <Companymanagment />
              </App>
            </Route>
            <Route exact path="/access-management">
              <App>
                <AccessManagement />
              </App>
            </Route>
            <Route exact path="/project-management">
              <App>
                <ProjectManagement />
              </App>
            </Route>
            <Route exact path="/user-management">
              <App>
                <UserManagement />
              </App>
            </Route>
            <Route exact path="/profile-management">
              <App>
                <ProfileManagement />
              </App>
            </Route>
            <Route exact path="/asset-management">
              <App>
                <Inventory />
                  {/* <AssetManagement /> */}
              </App>
            </Route>
            <Route exact path="/asset-old">
              <App>
                {/* <AssetManagement /> */}
                <Inventory />
              </App>
            </Route>
            <Route exact path="/inspection">
              <App>
                <Inspection />
              </App>
            </Route>
            <Route exact path="/inspection/plan">
              <App>
                <Plan />
              </App>
            </Route>
            <Route exact path="/inspection/map">
              <Redirect to="/map" />
            </Route>
            <Route exact path="/inventory">
              <Redirect to="/asset-management" />
            </Route>
            {/* Map dashboard route (requires active plan). */}
            <Route exact path="/map">
              <App>
                <RequirePlan>
                  <InspectionMap />
                </RequirePlan>
              </App>
            </Route>
            <Route exact path="/data/gallery">
              <App>
                <RequirePlan>
                  <Data />
                </RequirePlan>
              </App>
            </Route>
            <Route exact path="/vegetation/data/gallery">
              <App>
                <DataVegetation />
              </App>
            </Route>
            <Route exact path="/data/summary">
              <App>
                <RequirePlan>
                  <Summary />
                </RequirePlan>
              </App>
            </Route>
            <Route exact path="/data/import">
              <App>
                <RequirePlan>
                  <DataImport />
                </RequirePlan>
              </App>
            </Route>
            <Route exact path="/vegetation/data/summary">
              <App>
                <SummaryVegetation />
              </App>
            </Route>
            <Route exact path="/vegetation/data/import">
              <App>
                <DataImportVegetation />
              </App>
            </Route>
            <Route exact path="/lidar/data/summary">
              <App>
                <SummaryLidar />
              </App>
            </Route>
            <Route exact path="/lidar/data/import">
              <App>
                <DataImportLidar />
              </App>
            </Route>
            <Route exact path="/lidar/data/gallery">
              <App>
                <GalleryLidar />
              </App>
            </Route>
            <Route exact path="/inspection/create-plan">
              <App>
                <CreatePlan />
              </App>
            </Route>
            <Route exact path="/inspection/capture">
              <App>
                <Capture />
              </App>
            </Route>
            <Route exact path="/inspection/capture/summary">
              <App>
                <CaptureSummary />
              </App>
            </Route>
            <Route exact path="/inspection/capture/turbine">
              <App>
                <Turbine />
              </App>
            </Route>
            <Route exact path="/inspection/capture/imageGallery">
              <App>
                <ImageGallery />
              </App>
            </Route>
            <Route exact path="/vegetation/inspection/result">
              <App>
                <VegetationReport />
              </App>
            </Route>
            <Route exact path="/lidar/inspection/result">
              <App>
                <LidarReport />
              </App>
            </Route>
            <Route exact path="/lidar/analyse/summary">
              <App>
                <LidarAnalyse />
              </App>
            </Route>
            <Route exact path="/vegetation/inspection/result/map">
              <App>
                <VegetationReportMap />
              </App>
            </Route>
            <Route exact path="/lidar/inspection/result/map">
              <App>
                <LidarReportMap />
              </App>
            </Route>
            <Route exact path="/inspection/result">
              <App>
                <RequirePlan>
                  <Report />
                </RequirePlan>
              </App>
            </Route>
            <Route exact path="/inspection/result/report">
              <App>
                <RequirePlan>
                  <ReportsGeneration />
                </RequirePlan>
              </App>
            </Route>
            <Route exact path="/profile-management">
              <App>
                <ProfileManagement />
              </App>
            </Route>
            <Route exact path="/analyse/summary">
              <App>
                <RequirePlan>
                  <ProcessSummary />
                </RequirePlan>
              </App>
            </Route>
            <Route exact path="/analyse/review">
              <App>
                <RequirePlan>
                  <Review />
                </RequirePlan>
              </App>
            </Route>
            <Route exact path="/analyse/change-analysis">
              <App>
                <RequirePlan>
                  <ChangeAnalysis />
                </RequirePlan>
              </App>
            </Route>
            <Route exact path="/analyse/annotation">
              <App>
                <RequirePlan>
                  <Annotation />
                </RequirePlan>
              </App>
            </Route>
            <Route exact path="/analyse/tool">
              <App>
                <RequirePlan>
                  <AnalseTool />
                </RequirePlan>
              </App>
            </Route>
            <Route exact path="/inspection/process">
              <App>
                <RequirePlan>
                  <Process />
                </RequirePlan>
              </App>
            </Route>
            <Route exact path="/asset-type">
              <App>
                <AssetCreation />
              </App>
            </Route>
            <Route exact path="/asset-class">
              <App>
                <AssetClass />
              </App>
            </Route>
            <Route exact path="/asset-type/create">
              <App>
                <AssetCreationAdd />
              </App>
            </Route>{" "}
            <Route exact path="/asset-type/edit">
              <App>
                <AssetCreationEdit />
              </App>
            </Route>
            <Route exact path="/default-configuration">
              <App>
                <DefaultConfiguration />
              </App>
            </Route>
            <Route exact path="/client-configuration">
              <App>
                <ClientConfiguration />
              </App>
            </Route>
            <Route exact path="/inspection/plan/default-configuration">
              <App>
                <DefaultConfigurationPlan />
              </App>
            </Route>
            <Route exact path="/inspection/enhanced-thermal">
              <App>
                <RequirePlan>
                  <Enhanced />
                </RequirePlan>
              </App>
            </Route>
            <Route exact path="/asset-health/assets">
              <App>
                <AssetsDash />
              </App>
            </Route>
            <Route exact path="/asset-health/portfolio">
              <App>
                <Portfolio />
              </App>
            </Route>
            <Route exact path="/asset-health/damage">
              <App>
                <DamageDash />
              </App>
            </Route>
            {/* login */}
            <Route exact path="/login">
              <LoginApp>
                <Login />
              </LoginApp>
            </Route>
            <Route exact path="/forgot-password">
              <LoginApp>
                <ForgotPassword />
              </LoginApp>
            </Route>
            <Route exact path="/update-password">
              <LoginApp>
                <ResetPassword />
              </LoginApp>
            </Route>
          </Switch>
        </BrowserRouter>
      </Provider>
    </Fragment>
  );
};
ReactDOM.render(<Root />, document.getElementById("root"));
